/**
 * SAB-1003: the Sleeper-style game line — "Sun 4:25 PM vs DEN" / "Sun 1:00 PM @ KC".
 *
 * ── WHY A LIB AND NOT A COMPONENT DETAIL ────────────────────────────────────
 *
 * Three surfaces render a player row today and each built its own sub-line: My Team shows
 * team abbr + kickoff, the Matchup starter cell shows `team || position` (never both), and
 * the Matchup bench cell shows nothing at all. That is three answers to one question. The
 * formatting lives here so the three rows can converge without agreeing by coincidence.
 */

export interface GameContext {
  /** ISO kickoff from nfl_games.scheduled. Null when this team has no game this week. */
  scheduled: string | null;
  /** nfl_games.status. 'scheduled' means not started. */
  status: string | null;
  /** The OTHER team's alias, e.g. "DEN". Null when unknown or on bye. */
  opponentAlias: string | null;
  /** True when the player's team is the home side. Null when unknown. */
  isHome: boolean | null;
}

/**
 * "Sun 4:25 PM", in Eastern.
 *
 * Hard-pinned to America/New_York rather than the viewer's zone, deliberately: NFL kickoff
 * times are quoted in Eastern everywhere in the sport, and a west-coast manager reading
 * "Sun 1:25 PM" for the 4:25 window would be reading a different number from everyone
 * else in the league chat. Lifted verbatim from NFLMyTeamPage so the surfaces cannot drift.
 */
export function formatKickoff(isoStr: string | null | undefined): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  return `${day} ${time}`;
}

/**
 * The full line. Returns '' when there is nothing true to say.
 *
 * `vs` / `@` is driven by isHome, and when isHome is NULL the opponent is omitted entirely
 * rather than guessed — showing "vs DEN" for an away game is worse than showing the time
 * alone, because it reads as fact rather than as missing data.
 */
export function gameLine(ctx: GameContext | null | undefined): string {
  if (!ctx || !ctx.scheduled) return '';
  const when = formatKickoff(ctx.scheduled);
  if (!ctx.opponentAlias || ctx.isHome === null || ctx.isHome === undefined) return when;
  const side = ctx.isHome ? 'vs' : '@';
  return when ? `${when} ${side} ${ctx.opponentAlias}` : `${side} ${ctx.opponentAlias}`;
}

/**
 * "RB · KC (5)" — the Sleeper player line.
 *
 * The bye NUMBER comes from nfl_franchises.bye_week_2026, a cached column, while a row's
 * "is on bye THIS week" flag is derived from the absence of an nfl_games row. Those are two
 * different sources for one fact and could in principle disagree; they do not, because
 * 20260629041351 populated bye_week_2026 from exactly that absence, over the same table.
 * Noted rather than deduplicated: the cached column is what makes the number available
 * without querying all 18 weeks.
 */
export function playerMetaLine(
  position: string | null | undefined,
  teamAlias: string | null | undefined,
  byeWeek: number | null | undefined,
): string {
  const parts: string[] = [];
  if (position) parts.push(position);
  if (teamAlias) parts.push(teamAlias);
  const head = parts.join(' · ');
  // A bye number with no team to attach it to is noise, so it rides with the team or not
  // at all. `(0)` is not a real bye week and is treated as absent.
  return byeWeek && teamAlias ? `${head} (${byeWeek})` : head;
}

/**
 * A game is live when it has left 'scheduled' but has not reached any finished state.
 *
 * Defined by EXCLUSION, matching the heuristic already used on My Team and Matchups: a
 * status nobody anticipated (e.g. a new provider value for halftime) then reads as live,
 * which polls too often. The inverse — an allowlist of live states — would read an
 * unknown status as finished and stop updating a game that is still being played.
 */
const FINISHED = new Set(['closed', 'complete', 'completed', 'final', 'cancelled', 'postponed']);
export function isGameLive(status: string | null | undefined): boolean {
  const s = String(status || '').toLowerCase();
  return s !== '' && s !== 'scheduled' && !FINISHED.has(s);
}

/**
 * "J. McCarthy" — the mobile form of a player's name.
 *
 * SAB-1015: lifted out of NFLMyTeamPage, which held it as a private function and was the
 * ONLY caller passing shortName to NFLPlayerMeta. The matchup rows never did, so on a phone
 * they rendered full names into a column with roughly 87px to spend and truncated mid-word.
 * Abbreviating beats truncating there: "M. Harrison Jr." fits and still identifies the
 * player, where "Marvin Harri…" identifies nobody.
 *
 * NFLPlayerMeta exists so those rows converge by construction rather than by three people
 * remembering; a helper only one of them can reach defeats that.
 *
 * A single-word name is returned unchanged — "Cardinals" must not become "C. ".
 */
export function shortPlayerName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
}
